import { USER_JWT_TOKEN_COOKIE_NAME } from "~/components/_refactored/auth/constants";
import { useAuthStore } from "~/components/_refactored/auth/store";
import { GraphQLError, GraphQLUserError } from "~/components/_refactored/error";
import { useUserStore } from "~/stores/user";
import { redactSensitiveFields } from "~/helpers/jsutil";

// Auth tokens, codes, passwords and uploaded file contents whose values are masked as "[REDACTED]"
// before a mutation is logged — never their values in logs.
const SENSITIVE_MUTATION_VARIABLES = [
  "password",
  "newPassword",
  "currentPassword",
  "passwordRecoveryCode",
  "emailConfirmationCode",
  "code",
  "token",
  "regToken",
  "linkingToken",
  "contentBase64",
];

export default function (context, inject) {
  const { app, $joszaki, $logger, $sentry, i18n, $pinia } = context;

  /**
   * On the server the request's cookie header never changes during the
   * render, so a token that loadAuth rejected and logged out would still be
   * read from it and fail every query. The auth store holds the token loadAuth
   * accepted (or null after logout).
   */
  function getAuthToken() {
    if (process.server) {
      return useAuthStore($pinia).userToken;
    }
    return app.$cookie.get(USER_JWT_TOKEN_COOKIE_NAME);
  }

  // Helper function to get the locale for GraphQL requests
  // Checks for admin locale override in localStorage (client-side only)
  // Only applies admin locale if user is actually an admin
  function getGraphQLLocale() {
    if (process.client) {
      const adminLocale = localStorage.getItem("admin_graphql_locale");
      if (adminLocale) {
        try {
          const userStore = useUserStore($pinia);
          if (userStore.isAdmin) {
            return adminLocale;
          }
        } catch (error) {
          // If we can't check admin status, don't use admin locale for safety
          $logger.warn(
            "Could not check admin status for locale override",
            error
          );
        }
      }
    }
    return i18n.locale;
  }

  const statusCodeForErrorCode = {
    USER_ERROR: 400,
    BAD_USER_INPUT: 400,
    BAD_REQUEST: 400,
    GRAPHQL_VALIDATION_FAILED: 400,
    UNAUTHENTICATED: 401,
    FORBIDDEN: 403,
    ENTITY_NOT_FOUND: 404,
    INTERNAL_SERVER_ERROR: 500,
  };

  function reportResponseErrorsIfAny(err) {
    if (!err.response?.errors) {
      return;
    }
    err.response.errors.forEach((error) => {
      const wrappedError = new GraphQLError(error);

      wrappedError.reportToSentry($sentry);
    });
  }

  /**
   * Flattens the error parameters to make it usable in i18n
   * Eg. flattenForI18n({ targetZone: { id: 123, name: "Name" } })
   *      => { targetZoneId: 123, targetZoneName: "Name" }
   * @param {*} obj The object to flatten
   * @param {*} parentKey Used to build the key recursively
   * @param {*} result The result object to store the flattened key-value pairs
   * @returns The flattened object
   */
  function flattenErrorParams(obj = {}, parentKey = "", result = {}) {
    for (const [key, value] of Object.entries(obj)) {
      // For nested keys, capitalize only the first letter of the current key
      const newKey =
        parentKey === ""
          ? key
          : parentKey + key.charAt(0).toUpperCase() + key.slice(1);

      if (value && typeof value === "object" && !Array.isArray(value)) {
        flattenErrorParams(value, newKey, result);
      } else {
        result[newKey] = value;
      }
    }
    return result;
  }

  function getErrorMessage(error) {
    if (
      !error.message ||
      error.message.startsWith("Error while fetching subquery from service")
    ) {
      return null;
    }

    console.info(
      "Error extension",
      error?.extensions?.data,
      flattenErrorParams(error?.extensions?.data ?? {})
    );

    if (app.i18n.te(`error.messages.${error.message}`)) {
      return app.i18n.t(
        `error.messages.${error.message}`,
        flattenErrorParams(error?.extensions?.data ?? {})
      );
    }

    return error.message;
  }

  function handleAndGetErrorMessage(
    err,
    {
      showErrorAlert,
      showErrorPage,
      showErrorToast,
      actionErrorMessage,
      skipErrorPageOnAuthError,
    }
  ) {
    // Currently the API returns errors inconsistently, later we should only use err.response.errors
    const errors = [
      ...(err?.response?.errors ?? []),
      ...(err?.graphQLErrors ?? []),
      ...(err?.userErrors ?? []),
      err,
    ];

    $sentry.addBreadcrumb({
      category: "graphql",
      data: errors,
      level: "error",
    });

    const error = errors[0];
    const errorCode = error.code ?? error.extensions?.code;

    const errorCodeMessage = app.i18n.te(`error.codes.${errorCode}`)
      ? app.i18n.t(`error.codes.${errorCode}`)
      : app.i18n.t("error.codes.UNKNOWN");
    const errorMessage = getErrorMessage(error);

    const messagePrefix = actionErrorMessage ? `${actionErrorMessage} ` : "";
    const combinedErrorMessage = `${messagePrefix}${
      errorMessage ?? errorCodeMessage
    }`;

    if (showErrorToast) {
      $joszaki.toast({
        message: combinedErrorMessage,
        type: "error",
      });
    }

    if (showErrorAlert) {
      $joszaki.alert({
        title: errorCodeMessage,
        message: `${messagePrefix}${errorMessage ?? errorCodeMessage}`,
        type: "error",
      });
    }

    const errorPageVisible =
      showErrorPage ||
      (!skipErrorPageOnAuthError &&
        ["UNAUTHENTICATED", "FORBIDDEN"].includes(errorCode));

    if (errorPageVisible) {
      context.error({
        statusCode: statusCodeForErrorCode[errorCode] ?? 500,
        message: errorCodeMessage,
        detailedMessage: `${messagePrefix}${errorMessage ?? ""}`,
      });
    }
    return combinedErrorMessage;
  }

  async function mutate(
    mutation,
    variables = {},
    {
      showErrorAlert = false,
      showErrorToast = false,
      showErrorPage = false,
      showSuccessToast = false,
      showSuccessAlert = false,
      errorMessage = null,
      successMessage = null,
      skipErrorPageOnAuthError = false,
    } = {}
  ) {
    const mutationName = mutation.definitions?.[0]?.name?.value;
    const requestHeaders = {
      "Apollo-Require-Preflight": "true", // https://www.apollographql.com/docs/router/configuration/csrf/#configuring-csrf-prevention-in-the-router
      "x-locale": getGraphQLLocale(),
    };

    const token = getAuthToken();
    if (token) {
      requestHeaders.authorization = `Bearer ${token}`;
    }

    try {
      $logger.info("mutation called", {
        mutationName,
        variables: redactSensitiveFields(
          variables,
          SENSITIVE_MUTATION_VARIABLES
        ),
      });

      const result = await context.$graphql.default.request(
        mutation,
        variables,
        requestHeaders
      );
      if (result[mutationName]?.userErrors) {
        throw result[mutationName];
      }
      if (showSuccessToast) {
        $joszaki.toast({
          message: successMessage ?? i18n.t("common.operationSuccess"),
          type: "success",
        });
      }
      if (showSuccessAlert) {
        $joszaki.alert({
          message: successMessage ?? i18n.t("common.operationSuccess"),
          type: "success",
        });
      }
      return Promise.resolve(result[mutationName]);
    } catch (err) {
      reportResponseErrorsIfAny(err);

      if (err.userErrors) {
        err.userErrors.forEach((error) => {
          const wrappedError = new GraphQLUserError({ ...error, mutationName });

          wrappedError.reportToSentry(context.$sentry);
        });
      }

      const message = handleAndGetErrorMessage(err, {
        showErrorAlert,
        showErrorPage,
        showErrorToast,
        actionErrorMessage: errorMessage,
        skipErrorPageOnAuthError,
      });

      return Promise.reject(message);
    }
  }

  async function query(
    query,
    variables = {},
    {
      showErrorAlert,
      showErrorPage,
      showErrorToast,
      errorMessage,
      skipErrorPageOnAuthError = false,
    } = {}
  ) {
    const queryStartTime = new Date();
    try {
      const locale = getGraphQLLocale();
      const requestHeaders = {
        "x-locale": locale,
      };

      if (process.server) {
        $logger.info(
          `GraphQL query start: ${query.definitions?.[0]?.name?.value} locale: ${locale}`
        );
        const { requestId } = context.req;
        requestHeaders["x-rid"] = requestId;
      }

      const token = getAuthToken();
      if (token) {
        requestHeaders.authorization = `Bearer ${token}`;
      }

      const result = await context.$graphql.default.request(
        query,
        variables,
        requestHeaders
      );
      return Promise.resolve(result);
    } catch (err) {
      reportResponseErrorsIfAny(err);

      const message = handleAndGetErrorMessage(err, {
        showErrorAlert,
        showErrorPage,
        showErrorToast,
        actionErrorMessage: errorMessage,
        skipErrorPageOnAuthError,
      });

      return Promise.reject(message);
    } finally {
      if (process.server) {
        const queryEndTime = new Date();
        const queryDuration = queryEndTime.getTime() - queryStartTime.getTime();
        $logger.info(
          `GraphQL query end: ${query.definitions?.[0]?.name?.value} --- query duration: ${queryDuration} ms`
        );
      }
    }
  }

  inject("mutate", mutate);
  inject("query", query);
}
